import {
    ChangeDetectionStrategy,
    Component,
    inject,
    OnInit,
} from '@angular/core';
import {
    FormGroup,
    FormControl,
    FormsModule,
    ReactiveFormsModule,
} from '@angular/forms';
import { FormGroupType } from '../../../core/models/form-group-type';
import { ApplicationCreationRequest } from '../shared/models/requests/application-creation-request';
import { ApplicationPreparationQuestionnaireService } from '../shared/services/application-preparation-questionnaire/application-preparation-questionnaire.service';
import { StepNavigationService } from '../shared/services/step-navigation/step-navigation.service';
import { finalize, forkJoin, take, takeUntil, tap } from 'rxjs';
import { MatButtonModule } from '@angular/material/button';
import { MatError, MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDialog } from '@angular/material/dialog';
import { DropdownOption } from '../../../core/models/dropdown-option';
import { DropdownService } from '../../../core/services/resources/dropdown.service';
import { RegexInputDirective } from '../../../shared/directives/regex-input/regex-input.directive';
import { RegExPattern } from '../../../core/models/regexp-pattern/regex-pattern';
import { ApplicationPreparationBaseComponent } from '../../../shared/components/application-preparation-base/application-preparation-base.component';
import { AppStorageService } from '../../../core/services/app-storage/app-storage.service';
import { FrnSearchDialogComponent } from '../shared/components/frn-search-dialog/frn-search-dialog.component';
import { FrnEntitySearchResponse } from '../../../core/services/uls/models/frn-entity-search-response';
import {
    isFalsyOrWhitespace,
    padFrnWithLeadingZeros,
    tryParseToNumber,
} from '../../../core/functions/string-helpers';
import { ULSService } from '../../../core/services/uls/uls.service';
import { ModalComponent } from '../../../shared/components/modal/modal.component';
import {
    QuickStartErrorMessages,
    QuickStartFrnNotFoundModalMessages,
} from './constants/quick-start-messages';

@Component({
    selector: 'app-quick-start',
    templateUrl: './quick-start.component.html',
    styleUrl: './quick-start.component.scss',
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        MatSelectModule,
        MatFormFieldModule,
        MatInputModule,
        FormsModule,
        ReactiveFormsModule,
        MatButtonModule,
        MatTooltipModule,
        MatIconModule,
        MatError,
        RegexInputDirective,
    ],
})
export class QuickStartComponent
    extends ApplicationPreparationBaseComponent
    implements OnInit
{
    radioServiceCodes: DropdownOption[] = [];
    applicantTypeOptions: DropdownOption[] = [];
    selectedFrnEntity: FrnEntitySearchResponse | null = null;

    private isFrnValidationInProgress = false;
    private readonly dropdownService = inject(DropdownService);
    private readonly dialog = inject(MatDialog);
    private readonly ulsService = inject(ULSService);

    override form: FormGroup<
        Partial<FormGroupType<ApplicationCreationRequest>>
    > = new FormGroup<Partial<FormGroupType<ApplicationCreationRequest>>>({
        fccRegistrationNumber: new FormControl(null),
        radioServiceCode: new FormControl(null),
        applicantTypeCode: new FormControl(null),
    });

    /**
     * This is the final step and must result in form submission.
     */
    override shouldSubmit(): boolean {
        return true;
    }

    /**
     * No timeout needed. Timeouts are only applicable to non-new applications, which can take a long time to create.
     */
    override shouldAddTimeout(): boolean {
        return false;
    }

    regExPattern = RegExPattern;

    constructor() {
        super(
            inject(StepNavigationService),
            inject(ApplicationPreparationQuestionnaireService),
            inject(AppStorageService),
        );
    }

    override ngOnInit(): void {
        super.ngOnInit();

        this.loadDropdownData();
        this.setupFrnFieldListener();
    }

    getFrnError(): string | undefined {
        const control = this.form.controls.fccRegistrationNumber;
        if (!control?.errors || (!control.touched && !control.dirty)) {
            return undefined;
        }

        const firstErrorKey = Object.keys(control.errors)[0];
        return QuickStartErrorMessages.frn[
            firstErrorKey as keyof typeof QuickStartErrorMessages.frn
        ];
    }

    protected override handleApplicationPreparationStepContinuation(): void {
        if (!this.form.valid) {
            this.form.markAllAsTouched();
            return;
        }

        // If the FRN value is falsy or the FRN has already been validated,
        // continue the application preparation without searching for the FRN
        const frnValue = this.form.controls.fccRegistrationNumber?.value;
        if (isFalsyOrWhitespace(frnValue) || this.isFrnAlreadyValidated()) {
            super.handleApplicationPreparationStepContinuation();
            return;
        }

        // If the FRN number is null or the FRN validation is in progress, do not re-search for the FRN
        const frnNumber = tryParseToNumber(frnValue);
        if (frnNumber === null || this.isFrnValidationInProgress) {
            return;
        }

        // Start the FRN validation process
        this.isFrnValidationInProgress = true;
        this.ulsService
            .searchEntities({ frn: frnNumber })
            .pipe(
                take(1),
                takeUntil(this.destroy$),
                finalize(() => {
                    this.isFrnValidationInProgress = false;
                }),
            )
            .subscribe({
                next: (results) => {
                    if (results?.length > 0) {
                        this.applyValidatedFrnSearchResult(results, frnNumber);
                        super.handleApplicationPreparationStepContinuation();
                    } else {
                        this.openFrnNotFoundModal();
                    }
                },
                error: () => {
                    this.openFrnNotFoundModal();
                },
            });
    }

    private loadDropdownData(): void {
        forkJoin({
            radioServiceCodes: this.dropdownService.getRadioServiceCodes(),
            applicantTypeOptions: this.dropdownService.getApplicantTypeCodes(),
        })
            .pipe(
                takeUntil(this.destroy$),
                tap((result) => {
                    this.radioServiceCodes = result.radioServiceCodes;
                    this.applicantTypeOptions = result.applicantTypeOptions;
                }),
            )
            .subscribe();
    }

    openFrnSearchDialog(): void {
        if (this.dialog.openDialogs.length > 0) {
            return;
        }

        const dialogRef = this.dialog.open(FrnSearchDialogComponent, {
            width: '1300px',
            disableClose: true,
            data: {
                frn: this.form.controls.fccRegistrationNumber?.value,
            },
        });

        // When the dialog is closed, set the FRN number field and the selected FRN entity
        dialogRef
            .afterClosed()
            .pipe(takeUntil(this.destroy$))
            .subscribe((result?: FrnEntitySearchResponse) => {
                if (result) {
                    this.selectedFrnEntity = result;
                    this.patchQuickStartFormFromFrnEntity(result);
                    this.clearFrnError();
                }
            });
    }

    /**
     * When the user manually changes the FRN field, clear the selected FRN entity
     * so we re-validate on Next and don't reuse stale validation state.
     */
    private setupFrnFieldListener(): void {
        this.form.controls.fccRegistrationNumber?.valueChanges
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => {
                this.selectedFrnEntity = null;
            });
    }

    /**
     * Check if the FRN has already been validated and is the same as the current selected FRN entity.
     */
    private isFrnAlreadyValidated(): boolean {
        const frnValue = this.form.controls.fccRegistrationNumber?.value;
        const frnNumber = tryParseToNumber(frnValue);

        return (
            this.selectedFrnEntity !== null &&
            frnNumber !== null &&
            this.selectedFrnEntity.frn === frnNumber
        );
    }

    /**
     * Stores the ULS search match and syncs the quick start form from the entity.
     * The caller is responsible for calling the base continuation.
     */
    private applyValidatedFrnSearchResult(
        results: FrnEntitySearchResponse[],
        frnNumber: number,
    ): void {
        this.selectedFrnEntity =
            results.find((result) => result.frn === frnNumber) ?? results[0];
        this.clearFrnError();
        this.patchQuickStartFormFromFrnEntity(this.selectedFrnEntity);
    }

    /**
     * Opens the FRN not found modal when the FRN is not found.
     *
     * If the user selects Next, proceed without a FRN.
     * If the user selects Cancel, clear the FRN field and error.
     */
    private openFrnNotFoundModal(): void {
        if (this.dialog.openDialogs.length > 0) {
            return;
        }

        this.dialog
            .open(ModalComponent, {
                disableClose: true,
                data: {
                    title: QuickStartFrnNotFoundModalMessages.title,
                    content: QuickStartFrnNotFoundModalMessages.content,
                    primaryAction: {
                        title: QuickStartFrnNotFoundModalMessages.primaryActionTitle,
                    },
                },
            })
            .afterClosed()
            .pipe(takeUntil(this.destroy$))
            .subscribe((proceedWithoutFrn?: boolean) => {
                if (proceedWithoutFrn) {
                    this.clearFrnFromForm();
                    super.handleApplicationPreparationStepContinuation();
                } else {
                    this.handleFrnNotFoundCancel();
                }
            });
    }

    /**
     * Clears the FRN field, selected entity, and validation error from the quick start form.
     */
    private clearFrnFromForm(): void {
        this.form.controls.fccRegistrationNumber?.setValue(null, {
            emitEvent: false,
        });
        this.selectedFrnEntity = null;
        this.clearFrnError();
    }

    /**
     * Patches FRN and applicant type from a ULS entity.
     * Applicant type is always overwritten with the entity's applicant type, even when it is null.
     */
    private patchQuickStartFormFromFrnEntity(
        entity: FrnEntitySearchResponse,
    ): void {
        const patch: Partial<ApplicationCreationRequest> = {
            fccRegistrationNumber: padFrnWithLeadingZeros(
                entity.frnChar ?? entity.frn?.toString() ?? null,
            ),
            applicantTypeCode: entity.applicantTypeCode ?? null,
        };

        this.form.patchValue(patch, { emitEvent: false });
    }

    /**
     * Clears the FRN field and error when the user selects Cancel in the FRN not found modal.
     */
    private handleFrnNotFoundCancel(): void {
        this.form.controls.fccRegistrationNumber?.setValue(null, {
            emitEvent: false,
        });
        this.selectedFrnEntity = null;
        this.handleFrnError('notFound');
    }

    private handleFrnError(
        errorKey: keyof typeof QuickStartErrorMessages.frn,
    ): void {
        const message = QuickStartErrorMessages.frn[errorKey];
        this.form.controls.fccRegistrationNumber?.setErrors({
            [errorKey]: message,
        });
        this.form.controls.fccRegistrationNumber?.markAsTouched();
    }

    private clearFrnError(): void {
        this.form.controls.fccRegistrationNumber?.setErrors(null);
    }
}
